import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, CircularProgress,
  Divider, List, ListItemButton, ListItemIcon, ListItemText
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import KeyIcon from '@mui/icons-material/VpnKey';
import { useAuth } from '../context/AuthContext';
import { getLoginOrganizations } from '../api';

const Login = () => {
  const { isAuthenticated, loading, login } = useAuth();
  const [params] = useSearchParams();
  const error = params.get('error');

  // Orgs the user can log directly into (Case 1). Best-effort: if this fails or is empty, we just show
  // the plain "Sign in" button (Case 2) — same graceful degradation as OrgSwitcher.
  const [orgs, setOrgs] = useState([]);
  useEffect(() => {
    let active = true;
    getLoginOrganizations()
      .then((res) => {
        if (active) setOrgs(res.data?.organizations || []);
      })
      .catch(() => {
        /* non-fatal — the picker just won't render */
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f6f7fb 0%, #ede9f7 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 440, width: '100%', overflow: 'hidden' }}>
        <Box sx={{ height: 6, background: 'linear-gradient(90deg, #6B46C1 0%, #9F7AEA 100%)' }} />
        <CardContent sx={{ p: 5, textAlign: 'center' }}>
          <Box component="img" src="/logo.png" alt="Milient" sx={{ height: 48, mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            SSO Simulator
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Sign in through the Common SSO-Gateway to explore login, entitlements,
            user sync, and M2M flows.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => login()}
          >
            Sign in with Milient SSO
          </Button>

          {orgs.length > 0 && (
            <>
              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  or sign in to an organization
                </Typography>
              </Divider>
              <List dense disablePadding sx={{ textAlign: 'left' }}>
                {orgs.map((org) => (
                  <ListItemButton
                    key={org.id}
                    onClick={() => login(org.milientOrgId)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <BusinessIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={org.name} secondary={org.milientOrgId} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          <Divider sx={{ my: 3 }} />
          <Button
            component={RouterLink}
            to="/m2m-public"
            size="small"
            startIcon={<KeyIcon />}
            sx={{ textTransform: 'none' }}
          >
            Test M2M token (no login)
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
